import { Search } from "@mui/icons-material";
import { Box, InputAdornment, TextField } from "@mui/material";
import { getGroupsList } from "actions/groups";
import { getOptions } from "actions/option";
import { getOrganisationsList } from "actions/organisation";
import { USER_TYPE } from "components/UserPage/utils";
import { CONTROL_MODE_STATUS_OPTIONS, UNIT_LIST_FITLERS, getModemUsageOptions } from "constants/unit";
import { FormField } from "empower-energy-owner-components";
import useDebounce from "hooks/useDebounce";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const styles = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  alignItems: "flex-end",

  "& .mui-field": {
    minWidth: "185px",
    maxWidth: "210px",
    flex: "1 1 185px",
  },

  "@media (max-width: 1200px)": {
    "& .mui-field": {
      flex: "1 1 calc(33% - 12px)",
    },
  },
  "@media (max-width: 768px)": {
    "& .mui-field": {
      flex: "1 1 calc(50% - 12px)",
    },
  },
  "@media (max-width: 500px)": {
    "& .mui-field": {
      flex: "1 1 100%",
    },
  },
};

const UnitFilter = ({ onChangeFilters, filters }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(filters);

  const profile = useSelector((state) => state.profile);
  const organisationList = useSelector(
    (state) => state.organisation?.list || [],
  );
  const groupList = useSelector((state) => state.groups?.list || []);
  const lifecycleStateList = useSelector(
    (state) => state.option?.lifecycle_state || [],
  );
  const ratePlanList = useSelector(
    (state) => state.option?.modem_rate_plan || [],
  );
  const installerList = useSelector(
    (state) => state.organisation?.installerList || [],
  );
  const channelOptions = useSelector((state) => state.option?.channel || []);

  const debouncedSearch = useDebounce(formData.search, 500);

  useEffect(() => {
    dispatch(getGroupsList());
    dispatch(getOptions());

    if (profile?.is_staff) {
      dispatch(getOrganisationsList());
      dispatch(getOrganisationsList(USER_TYPE.INSTALLER));
    }
  }, [profile?.is_staff, dispatch]);

  useEffect(() => {
    setFormData(filters);
  }, [filters]);

  useEffect(() => {
    if (filters.search !== debouncedSearch) {
      onChangeFilters({ ...formData, search: debouncedSearch });
    }
  }, [debouncedSearch]);

  const constructOptions = useCallback(
    (list = []) => [
      { value: "", label: "Select All" },
      ...list.filter((o) => o?.value !== "" && o?.label),
    ],
    [],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = {
        ...prev,
        search: "",
        paginationModel: {
          pageSize: prev?.paginationModel.pageSize,
          page: 0,
        },
        [name]: value,
      };

      if (name !== "search") {
        onChangeFilters(updated);
      }
      return updated;
    });
  };

  const handleSearchChange = (e) => {
    setFormData({
      ...UNIT_LIST_FITLERS,
      search: e.target.value,
    });
  };

  const renderSelect = (label, name, list) => (
    <FormField
      className="mui-field"
      label={label}
      name={name}
      type="select"
      variant="outlined"
      value={formData[name] ?? ""}
      onChange={handleChange}
      options={constructOptions(list)}
    />
  );

  return (
    <Box my={2}>
      <Box className="items-center row-wrap" sx={styles}>
        {profile?.is_staff &&
          renderSelect("Select Organisation", "organisation", organisationList)}

        {renderSelect("Select Group", "group", groupList)}

        {profile?.is_staff &&
          renderSelect("Select Installer", "installer", installerList)}

        {renderSelect("Select Lifecycle", "lifecycle", lifecycleStateList)}

        {renderSelect(
          "Select Control Mode",
          "control_mode_status",
          CONTROL_MODE_STATUS_OPTIONS,
        )}

        {profile?.is_staff &&
          renderSelect("Select Sales Channel", "channel", channelOptions)}

        {profile?.is_staff &&
          renderSelect("Select Rate Plan", "modem_rate_plan", ratePlanList)}
        
        {profile?.is_staff &&
          renderSelect("Modem Usage", "modem_projection_mb", getModemUsageOptions())}
        <Box sx={{ flexGrow: 1 }} />

        <TextField
          size="small"
          className="mui-field"
          label="Search"
          variant="outlined"
          value={formData.search}
          name="search"
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default UnitFilter;
